import { useEffect, useState } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, Chip, Alert, Grid } from "@mui/material";
import axios from "axios";
import moment from "moment";
import MaterialTable from "material-table";
import { Icons } from "../../../utils/Icon";

const formatDate = (value) => {
    if (!value) {
        return "-";
    }

    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : "-";
};

const formatDateTime = (value) => {
    if (!value) {
        return "-";
    }

    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD HH:mm:ss") : "-";
};

export default function ViewUtilityLicenseV2Form({ open, handleClose, rowData }) {
    const [detailData, setDetailData] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const fetchDetail = async () => {
            if (!open || !rowData?.id) {
                return;
            }

            setDetailData(null);
            setErrorMessage("");

            try {
                const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v2/utility-license/${encodeURIComponent(rowData.id)}`);
                setDetailData(response.data.data || null);
                setErrorMessage("");
            } catch (error) {
                setDetailData(null);
                if (error.response && error.response.data && error.response.data.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage("An error occurred while loading utility license v2 detail.");
                }
            }
        };

        fetchDetail();
    }, [open, rowData]);

    if (!rowData) {
        return null;
    }

    const activationColumns = [
        { title: "Device Name", field: "device_name" },
        { title: "Status", field: "status" },
        { title: "Activated At", field: "activated_at", render: (activation) => formatDateTime(activation.activated_at) },
        { title: "Last Verified At", field: "last_verified_at", render: (activation) => formatDateTime(activation.last_verified_at) },
        { title: "Last Token Expires At", field: "last_token_expires_at", render: (activation) => formatDateTime(activation.last_token_expires_at) },
        { title: "Released At", field: "released_at", render: (activation) => formatDateTime(activation.released_at) },
    ];

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">View Utility License v2 Information</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Utility License v2 Details" />
                </Divider>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="remarks"
                            label="License Code"
                            type="text"
                            fullWidth
                            value={detailData?.remarks || ""}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="product_code"
                            label="Product Code"
                            type="text"
                            fullWidth
                            value={detailData?.product_code || ""}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_code"
                            label="Customer Code"
                            type="text"
                            fullWidth
                            value={detailData?.customer_code || ""}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_name"
                            label="Customer Name"
                            type="text"
                            fullWidth
                            value={detailData?.customer_name || ""}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_email"
                            label="Customer Email"
                            type="text"
                            fullWidth
                            value={detailData?.customer_email || ""}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="status"
                            label="Main Status"
                            type="text"
                            fullWidth
                            value={detailData?.status || ""}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="device_status"
                            label="Device Status"
                            type="text"
                            fullWidth
                            value={detailData?.device_status || "-"}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="expires_at"
                            label="Expires At"
                            type="text"
                            fullWidth
                            value={formatDate(detailData?.expires_at)}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="offline_days"
                            label="Offline Days"
                            type="number"
                            fullWidth
                            value={detailData?.offline_days ?? ""}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                </Grid>
                <Divider style={{ paddingTop: "12px", paddingBottom: "8px" }}>
                    <Chip label="License Activations" />
                </Divider>
                <MaterialTable
                    title=""
                    icons={Icons}
                    columns={activationColumns}
                    data={detailData?.license_activations || []}
                    options={{
                        search: false,
                        paging: false,
                        sorting: false,
                        toolbar: false,
                        draggable: false,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "260px",
                        padding: "dense",
                    }}
                    localization={{
                        body: {
                            emptyDataSourceMessage: "No activation data to display",
                        },
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
